import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import {
    Store, Save, Image, Palette, Clock, Settings, Eye, RefreshCcw,
    Utensils, ShoppingBag, Layout, Plus, Trash2, Phone,
    Mail, Instagram, List, Grid, Landmark,
    Megaphone, Users, MapPin, Truck, Bell, Volume2, Monitor, Timer, CreditCard
} from "lucide-react";
import { toast } from "react-hot-toast";
import PushDispositivosPanel from "../components/ui/PushDispositivosPanel";
import { DEFAULT_STORE_LOGISTICS_CONFIG } from "../utils/storeLogistics";
import { DEFAULT_LOW_STOCK_ALERT } from "../utils/storeLowStockAlert";
import ImageUploadField from "../components/ImageUploadField";

const STOREFRONT_REFRESH_EVENT = "impulza:storefront-refresh";
const STOREFRONT_REFRESH_STORAGE_KEY = "impulza:storefront-refresh";

const STORE_THEME_PRESETS = [
    {
        name: 'Clasico',
        primaryColor: '#0f172a',
        storeBackgroundColor: '#f8fafc',
        cardColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
        modalColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' }
    },
    {
        name: 'Bodega',
        primaryColor: '#7f1d1d',
        storeBackgroundColor: '#f8f1ee',
        cardColors: { bg: '#fffaf8', border: '#e7c9c0', text: '#3f1d1d' },
        modalColors: { bg: '#fffaf8', border: '#d9b8ae', text: '#3f1d1d' }
    },
    {
        name: 'Oliva',
        primaryColor: '#51643a',
        storeBackgroundColor: '#f5f6ef',
        cardColors: { bg: '#fdfdf8', border: '#d8dec8', text: '#26301f' },
        modalColors: { bg: '#fcfcf6', border: '#cfd8bd', text: '#26301f' }
    },
    {
        name: 'Oceano',
        primaryColor: '#0f4c81',
        storeBackgroundColor: '#eef6fb',
        cardColors: { bg: '#ffffff', border: '#bfd7ea', text: '#12324a' },
        modalColors: { bg: '#f8fcff', border: '#a9c9e2', text: '#12324a' }
    },
    {
        name: 'Nocturno',
        primaryColor: '#d4a373',
        storeBackgroundColor: '#111827',
        cardColors: { bg: '#1f2937', border: '#374151', text: '#f9fafb' },
        modalColors: { bg: '#1f2937', border: '#4b5563', text: '#f9fafb' }
    },
];

const MODAL_STYLE_PRESETS = [
    { name: 'Clásico', bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
    { name: 'Oscuro', bg: '#1f2937', border: '#374151', text: '#f9fafb' },
    { name: 'Crema', bg: '#fffbeb', border: '#fcd34d', text: '#78350f' },
    { name: 'Azulado', bg: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
];

const DAYS_MAP = [
    { key: 'mon', label: 'Lunes' },
    { key: 'tue', label: 'Martes' },
    { key: 'wed', label: 'Miércoles' },
    { key: 'thu', label: 'Jueves' },
    { key: 'fri', label: 'Viernes' },
    { key: 'sat', label: 'Sábado' },
    { key: 'sun', label: 'Domingo' },
];

export default function TiendaConfig() {
    const { profile } = useAuth();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);

    // Estado para bancos disponibles
    const [banks, setBanks] = useState([]);
    // Estado para proveedores
    const [providers, setProviders] = useState([]);
    // Estado para sitios (sucursales)
    const [sites, setSites] = useState([]);
    // Estado para métodos de pago
    const [paymentMethods, setPaymentMethods] = useState([]);

    const [activeDay, setActiveDay] = useState('mon');

    // --- CARGA DE DATOS ---
    useEffect(() => {
        if (!profile?.organization_id) return;
        loadData();
        // eslint-disable-next-line
    }, [profile?.organization_id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const orgId = profile.organization_id;

            // 1. Cargar Bancos, Proveedores, Sitios y Métodos de Pago
            const [banksRes, provRes, sitesRes, pmRes] = await Promise.all([
                supabase.from('banks').select('id, name, cbu_alias, currency').eq('organization_id', orgId).eq('is_active', true),
                supabase.from('proveedores').select('id, razon_social').eq('organization_id', orgId).eq('is_active', true).order('razon_social'),
                supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).eq('is_active', true).order('nombre'),
                supabase.from('payment_methods').select('id, name').eq('organization_id', orgId).eq('is_active', true).order('name')
            ]);

            setBanks(banksRes.data || []);
            setProviders(provRes.data || []);
            setSites(sitesRes.data || []);
            setPaymentMethods(pmRes.data || []);

            // 2. Cargar Configuración
            const { data, error } = await supabase
                .from("store_config")
                .select("*")
                .eq("organization_id", orgId)
                .maybeSingle();

            if (error) throw error;

            if (!data) {
                // Crear configuración por defecto
                const slugBase = `tienda-${orgId.slice(0, 6)}`;
                const { data: created, error: createErr } =
                    await supabase
                        .from("store_config")
                        .insert({
                            organization_id: orgId,
                            slug: slugBase,
                            store_title: "Mi Tienda Online",
                            view_mode: "grid",
                            show_stock: true,
                            always_open: false,
                            store_type: 'retail',
                            open_hours: { weekly: {} },
                            theme_config: {
                                primaryColor: '#0f172a',
                                storeBackgroundColor: '#f8fafc',
                                cardColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
                                modalColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
                                allow_checkout_without_stock: false,
                                showPrices: true,
                                requireCuitForPriceView: false,
                                checkoutIdentityMode: 'phone',
                                logistics: DEFAULT_STORE_LOGISTICS_CONFIG,
                                carousel: { enabled: false, slides: [] },
                                about: { enabled: false, title: '', text: '', image: '' },
                                welcomePopup: {
                                    enabled: false,
                                    title: 'Bienvenidos',
                                    message: 'Gracias por visitar nuestra tienda. Mira nuestras novedades y promociones antes de hacer tu pedido.',
                                    buttonText: 'Ver tienda'
                                },
                                contactSection: { enabled: true },
                                locations: [], // Nueva lista de ubicaciones
                                enabled_payment_methods: [], // Start with empty array
                                payment_method_labels: {}
                            },
                            notification_config: { sound: true, push: false, toast: true },
                            delivery_estimation: { enabled: true, time: 45, unit: 'minutos' },
                            default_fulfillment_site_id: sitesRes.data?.[0]?.id || null
                        })
                        .select()
                        .single();

                if (createErr) throw createErr;
                setConfig(created);
            } else {
                const loadedConfig = { ...data };
                // Asegurar estructura de theme_config
                loadedConfig.theme_config = {
                    primaryColor: '#0f172a',
                    storeBackgroundColor: '#f8fafc',
                     cardColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
                     modalColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
                     allow_checkout_without_stock: false,
                     showPrices: true,
                     requireCuitForPriceView: false,
                     enable_brand_filter: false,
                     enable_sorting: false,
                     checkoutIdentityMode: 'phone',
                     logistics: DEFAULT_STORE_LOGISTICS_CONFIG,
                    carousel: { enabled: false, slides: [] },
                    about: { enabled: false, title: '', text: '', image: '' },
                    welcomePopup: {
                        enabled: false,
                        title: 'Bienvenidos',
                        message: 'Gracias por visitar nuestra tienda. Mira nuestras novedades y promociones antes de hacer tu pedido.',
                        buttonText: 'Ver tienda'
                    },
                    contactSection: { enabled: true },
                    locations: [],
                    enabled_payment_methods: [],
                    payment_method_labels: {},
                    ...loadedConfig.theme_config
                };

                // Asegurar estructura notificaciones
                loadedConfig.notification_config = {
                    sound: true, push: false, toast: true,
                    ...loadedConfig.notification_config
                };

                // Asegurar estructura estimación entrega
                loadedConfig.delivery_estimation = {
                    enabled: false, time: 45, unit: 'minutos',
                    ...loadedConfig.delivery_estimation
                };

                loadedConfig.open_hours = loadedConfig.open_hours || { weekly: {} };
                if (!loadedConfig.open_hours.weekly) loadedConfig.open_hours.weekly = {};
                if (!loadedConfig.default_fulfillment_site_id) {
                    loadedConfig.default_fulfillment_site_id = sitesRes.data?.[0]?.id || null;
                }

                setConfig(loadedConfig);
            }
        } catch (e) {
            console.error(e);
            toast.error("No se pudo cargar la configuración");
        } finally {
            setLoading(false);
        }
    };

    const update = (field, value) => {
        setConfig((prev) => ({ ...prev, [field]: value }));
    };

    const updateTheme = (key, value) => {
        setConfig((prev) => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                modalColors: {
                    ...prev.theme_config?.modalColors,
                    [key]: value
                }
            }
        }));
    };

    const updateThemeField = (key, value) => {
        setConfig((prev) => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                [key]: value
            }
        }));
    };

    const updateCardTheme = (key, value) => {
        setConfig((prev) => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                cardColors: {
                    ...prev.theme_config?.cardColors,
                    [key]: value
                }
            }
        }));
    };

    // --- ACTUALIZADORES DE SECCIONES NUEVAS ---
    const updateSection = (sectionName, field, value) => {
        setConfig(prev => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                [sectionName]: {
                    ...prev.theme_config[sectionName],
                    [field]: value
                }
            }
        }));
    };

    // Actualizador de Notificaciones
    const updateNotif = (field, value) => {
        setConfig(prev => ({
            ...prev,
            notification_config: {
                ...prev.notification_config,
                [field]: value
            }
        }));
    };

    // Actualizador de Estimación Entrega
    const updateDeliveryEst = (field, value) => {
        setConfig(prev => ({
            ...prev,
            delivery_estimation: {
                ...prev.delivery_estimation,
                [field]: value
            }
        }));
    };


    // Carrusel
    const addSlide = () => {
        const currentSlides = config.theme_config.carousel?.slides || [];
        updateSection('carousel', 'slides', [...currentSlides, '']);
    };
    const updateSlide = (idx, val) => {
        const newSlides = [...(config.theme_config.carousel?.slides || [])];
        newSlides[idx] = val;
        updateSection('carousel', 'slides', newSlides);
    };
    const removeSlide = (idx) => {
        const newSlides = (config.theme_config.carousel?.slides || []).filter((_, i) => i !== idx);
        updateSection('carousel', 'slides', newSlides);
    };

    // Ubicaciones
    const addLocation = () => {
        const currentLocs = config.theme_config.locations || [];
        setConfig(prev => ({
            ...prev,
            theme_config: { ...prev.theme_config, locations: [...currentLocs, ''] }
        }));
    };
    const updateLocation = (idx, val) => {
        const currentLocs = [...(config.theme_config.locations || [])];
        currentLocs[idx] = val;
        setConfig(prev => ({
            ...prev,
            theme_config: { ...prev.theme_config, locations: currentLocs }
        }));
    };
    const removeLocation = (idx) => {
        const currentLocs = (config.theme_config.locations || []).filter((_, i) => i !== idx);
        setConfig(prev => ({
            ...prev,
            theme_config: { ...prev.theme_config, locations: currentLocs }
        }));
    };


    const applyPreset = (preset) => {
        setConfig((prev) => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                primaryColor: preset.primaryColor,
                storeBackgroundColor: preset.storeBackgroundColor,
                cardColors: preset.cardColors,
                modalColors: preset.modalColors
            }
        }));
    };

    const togglePaymentMethod = (pmId) => {
        const currentMethods = config.theme_config.enabled_payment_methods || [];
        const isEnabled = currentMethods.includes(pmId);
        
        const newMethods = isEnabled 
            ? currentMethods.filter(id => id !== pmId)
            : [...currentMethods, pmId];

        // Ensure enabled_payment_methods exists in theme_config update
        setConfig(prev => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                enabled_payment_methods: newMethods
            }
        }));
    };

    const updatePaymentMethodLabel = (pmId, value) => {
        setConfig(prev => ({
            ...prev,
            theme_config: {
                ...prev.theme_config,
                payment_method_labels: {
                    ...(prev.theme_config?.payment_method_labels || {}),
                    [pmId]: value
                }
            }
        }));
    };

    const currentSlots = useMemo(() => {
        return config?.open_hours?.weekly?.[activeDay] || [];
    }, [config, activeDay]);

    const updateSlots = (newSlots) => {
        const currentWeekly = config?.open_hours?.weekly || {};
        const newWeekly = { ...currentWeekly, [activeDay]: newSlots };
        update("open_hours", { ...config.open_hours, weekly: newWeekly });
    };

    const addSlot = () => {
        updateSlots([...currentSlots, { from: '09:00', to: '18:00' }]);
    };

    const removeSlot = (index) => {
        const newSlots = currentSlots.filter((_, i) => i !== index);
        updateSlots(newSlots);
    };

    const changeSlotValue = (index, field, value) => {
        const newSlots = [...currentSlots];
        newSlots[index] = { ...newSlots[index], [field]: value };
        updateSlots(newSlots);
    };

    // Helper para autocompletar datos bancarios
    const handleBankSelection = (bankId) => {
        update("default_bank_id", bankId);

        const bank = banks.find(b => b.id === bankId);
        if (bank && (!config.bank_details || config.bank_details.trim() === '')) {
            const details = `${bank.name}\n${bank.cbu_alias ? `CBU/Alias: ${bank.cbu_alias}` : ''}\nMoneda: ${bank.currency}`;
            update("bank_details", details);
            toast("Datos bancarios sugeridos en el campo de texto", { icon: '💡' });
        }
    };

    const save = async () => {
        if (!config) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from("store_config")
                .update({
                    slug: config.slug,
                    store_title: config.store_title,
                    store_type: config.store_type,
                    banner_url: config.banner_url,
                    logo_url: config.logo_url,
                    favicon_url: config.favicon_url,
                    whatsapp: config.whatsapp,
                    instagram: config.instagram,
                    notify_email: config.notify_email,
                    theme_config: config.theme_config,
                    notification_config: config.notification_config,
                    delivery_estimation: config.delivery_estimation, // NUEVO
                    show_stock: config.show_stock,
                    always_open: config.always_open,
                    shipping_cost: config.shipping_cost,
                    shipping_provider_id: config.shipping_provider_id,
                    free_shipping_threshold: config.free_shipping_threshold,
                    financing_text: config.financing_text,
                    bank_details: config.bank_details,
                    default_bank_id: config.default_bank_id,
                    view_mode: config.view_mode,
                    open_hours: config.open_hours,
                    default_fulfillment_site_id: config.default_fulfillment_site_id
                })
                .eq("organization_id", profile.organization_id);

            if (error) throw error;

            const refreshPayload = {
                organizationId: profile.organization_id,
                slug: config.slug,
                ts: Date.now()
            };

            localStorage.setItem(STOREFRONT_REFRESH_STORAGE_KEY, JSON.stringify(refreshPayload));
            window.dispatchEvent(new CustomEvent(STOREFRONT_REFRESH_EVENT, { detail: refreshPayload }));
            toast.success("Configuración guardada correctamente");
        } catch (e) {
            console.error(e);
            toast.error("Error al guardar cambios");
        } finally {
            setSaving(false);
        }
    };

    const previewUrl = useMemo(() => {
        if (!config?.slug) return "#";
        const baseUrl = window.location.origin;
        return `${baseUrl}/tienda/${config.slug}`;
    }, [config?.slug]);

    if (loading) return (
        <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
            <div className="animate-spin text-[#059669] mb-4"><Settings size={40} /></div>
            <p className="font-bold uppercase tracking-[0.3em] text-[var(--text-muted)] text-[10px]">Cargando Configuración...</p>
        </div>
    );

    if (!config) return <div className="p-6">No se pudo cargar la configuración.</div>;

    const isGastronomy = config.store_type === 'gastronomy';
    const lowStockAlert = { ...DEFAULT_LOW_STOCK_ALERT, ...(config.theme_config?.lowStockAlert || {}) };

    return (
        <div className="imp-page imp-container animate-nova-in px-3 py-3 sm:px-4 sm:py-4 lg:px-5">

            <div className="imp-header mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-[var(--surface)] border border-[color:var(--border)] rounded-md flex items-center justify-center shadow-sm shrink-0">
                        <Store size={18} className="text-[#10B981]" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-xl md:text-2xl font-black text-[var(--color-accent-dark)] tracking-tight uppercase truncate">Configuración de Tienda</h1>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">
                            Canal Digital — Personalización y reglas
                        </p>
                    </div>
                </div>

                <div className="imp-header-actions w-full md:w-auto">
                    <a
                        href={previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full md:w-auto px-4 py-2.5 rounded-md bg-[var(--surface)] border border-[color:var(--border)] text-[var(--text-default)] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                        <Eye size={16} />
                        Ver tienda
                    </a>

                    <button
                        data-onboarding-id="tienda-config-submit"
                        onClick={save}
                        disabled={saving}
                        className="w-full md:w-auto px-4 py-2.5 rounded-md bg-[#10B981] text-[#18181B] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <RefreshCcw size={16} className="animate-spin" /> : <Save size={16} />}
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">

                {/* === COLUMNA IZQUIERDA === */}
                <div className="lg:col-span-7 space-y-4">

                    {/* GIRO DEL NEGOCIO */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Layout size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Giro del Negocio</span>
                        </div>
                        <div className="p-4 sm:p-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    onClick={() => update('store_type', 'gastronomy')}
                                    className={`p-3.5 rounded-xl border-2 text-left transition-all ${config.store_type === 'gastronomy' ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200 ring-offset-1' : 'border-slate-100 hover:border-[color:var(--border)]'}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${config.store_type === 'gastronomy' ? 'bg-orange-500 text-white' : 'bg-[var(--surface-2)] text-[var(--text-muted)]'}`}>
                                        <Utensils size={20} />
                                    </div>
                                    <div className="font-black text-[var(--color-accent-dark)] uppercase text-xs tracking-wide">Envíos en el día</div>
                                    <p className="text-[10px] text-[var(--text-muted)] mt-1 leading-snug">Ideal delivery rápido / comidas. Estados: Preparando, Listo.</p>
                                </button>

                                <button
                                    onClick={() => update('store_type', 'retail')}
                                    className={`p-3.5 rounded-xl border-2 text-left transition-all ${config.store_type === 'retail' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 ring-offset-1' : 'border-slate-100 hover:border-[color:var(--border)]'}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${config.store_type === 'retail' ? 'bg-mint-400 text-white' : 'bg-[var(--surface-2)] text-[var(--text-muted)]'}`}>
                                        <ShoppingBag size={20} />
                                    </div>
                                    <div className="font-black text-[var(--color-accent-dark)] uppercase text-xs tracking-wide">Retail / Productos</div>
                                    <p className="text-[10px] text-[var(--text-muted)] mt-1 leading-snug">Ideal tiendas de ropa/objetos. Estados: Empaquetando, Enviado.</p>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* SECCIONES Y CONTENIDO */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Megaphone size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Contenido y Secciones</span>
                        </div>
                        <div className="p-4 sm:p-5 space-y-6">

                            {/* 0. IMÁGENES DE LA TIENDA (todo lo que no es foto de producto) */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] flex items-center gap-2">
                                    <Image size={14} /> Imágenes de la Tienda
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <ImageUploadField
                                            label="Banner superior (portada)"
                                            hint={
                                                config.theme_config.carousel?.enabled && (config.theme_config.carousel?.slides || []).length > 0
                                                    ? "El carrusel está activo y tiene slides: se muestra el carrusel en lugar de este banner."
                                                    : "Se muestra de fondo en el encabezado de la tienda. Ideal 1600×600 px."
                                            }
                                            value={config.banner_url || ""}
                                            onChange={(url) => update("banner_url", url)}
                                            organizationId={profile?.organization_id}
                                            prefix="banner"
                                            aspect="banner"
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <ImageUploadField
                                            label="Logo"
                                            hint="Se muestra sobre el banner. Usá PNG con fondo transparente."
                                            value={config.logo_url || ""}
                                            onChange={(url) => update("logo_url", url)}
                                            organizationId={profile?.organization_id}
                                            prefix="logo"
                                            aspect="logo"
                                            maxSizeMb={2}
                                        />
                                        <ImageUploadField
                                            label="Favicon"
                                            hint="Ícono de la pestaña del navegador. Cuadrado, 64×64 px."
                                            value={config.favicon_url || ""}
                                            onChange={(url) => update("favicon_url", url)}
                                            organizationId={profile?.organization_id}
                                            prefix="favicon"
                                            aspect="square"
                                            accept="image/png,image/x-icon,image/svg+xml,image/webp"
                                            maxSizeMb={1}
                                            compact
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 1. CARRUSEL */}
                            <div className="space-y-3 border-t border-slate-100 pt-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] flex items-center gap-2">
                                        <Image size={14} /> Pasarela de Fotos (Carrusel)
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={config.theme_config.carousel?.enabled || false}
                                        onChange={(e) => updateSection('carousel', 'enabled', e.target.checked)}
                                        className="w-4 h-4 cursor-pointer accent-slate-900"
                                    />
                                </div>
                                {config.theme_config.carousel?.enabled && (
                                    <div className="bg-[var(--surface)] p-3 rounded-xl border border-slate-100 animate-in fade-in">
                                        <div className="space-y-3">
                                            {(config.theme_config.carousel?.slides || []).length === 0 && (
                                                <p className="text-[10px] text-[var(--text-muted)]">
                                                    Sin slides todavía. Mientras el carrusel esté vacío se muestra el banner superior.
                                                </p>
                                            )}
                                            {(config.theme_config.carousel?.slides || []).map((slide, idx) => (
                                                <div key={idx} className="flex gap-2 items-start">
                                                    <div className="flex-1">
                                                        <ImageUploadField
                                                            label={`Slide ${idx + 1}`}
                                                            value={slide}
                                                            onChange={(url) => updateSlide(idx, url)}
                                                            organizationId={profile?.organization_id}
                                                            prefix={`slide-${idx + 1}`}
                                                            aspect="banner"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSlide(idx)}
                                                        title="Eliminar slide"
                                                        className="text-rose-500 p-2 mt-5 hover:bg-rose-50 rounded-lg shrink-0"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button onClick={addSlide} className="text-[10px] font-bold text-mint-600 uppercase flex items-center gap-1 hover:underline">
                                                <Plus size={12} /> Agregar Slide
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 2. NOSOTROS */}
                            <div className="space-y-3 border-t border-slate-100 pt-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] flex items-center gap-2">
                                        <Users size={14} /> Página "Nosotros"
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={config.theme_config.about?.enabled || false}
                                        onChange={(e) => updateSection('about', 'enabled', e.target.checked)}
                                        className="w-4 h-4 cursor-pointer accent-slate-900"
                                    />
                                </div>
                                {config.theme_config.about?.enabled && (
                                    <div className="bg-[var(--surface)] p-3 rounded-xl border border-slate-100 animate-in fade-in space-y-3">
                                        <input
                                            value={config.theme_config.about?.title || ''}
                                            onChange={(e) => updateSection('about', 'title', e.target.value)}
                                            className="w-full border rounded-lg px-3 py-2 text-sm font-bold"
                                            placeholder="Título (Ej: Nuestra Historia)"
                                        />
                                        <textarea
                                            value={config.theme_config.about?.text || ''}
                                            onChange={(e) => updateSection('about', 'text', e.target.value)}
                                            className="w-full border rounded-lg px-3 py-2 text-sm resize-none h-24"
                                            placeholder="Descripción de la empresa..."
                                        />
                                        <ImageUploadField
                                            label="Foto del equipo o del local"
                                            hint="Se muestra al costado del texto en la solapa “Nuestra Historia”."
                                            value={config.theme_config.about?.image || ''}
                                            onChange={(url) => updateSection('about', 'image', url)}
                                            organizationId={profile?.organization_id}
                                            prefix="nosotros"
                                            aspect="wide"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 3. CONTACTO Y UBICACIONES */}
                            <div className="space-y-3 border-t border-slate-100 pt-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] flex items-center gap-2">
                                        <MapPin size={14} /> Página "Contacto"
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={config.theme_config.contactSection?.enabled !== false} // Default true
                                        onChange={(e) => updateSection('contactSection', 'enabled', e.target.checked)}
                                        className="w-4 h-4 cursor-pointer accent-slate-900"
                                    />
                                </div>

                                {config.theme_config.contactSection?.enabled !== false && (
                                    <div className="bg-[var(--surface)] p-3 rounded-xl border border-slate-100 animate-in fade-in">
                                        <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] block mb-2">Sucursales / Puntos de Retiro</label>
                                        <div className="space-y-2">
                                            {(config.theme_config.locations || []).map((loc, idx) => (
                                                <div key={idx} className="flex gap-2">
                                                    <input
                                                        value={loc}
                                                        onChange={(e) => updateLocation(idx, e.target.value)}
                                                        className="flex-1 border rounded-lg px-3 py-2 text-xs"
                                                        placeholder="Ej: Av. Siempreviva 123, Springfield"
                                                    />
                                                    <button onClick={() => removeLocation(idx)} className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 size={14} /></button>
                                                </div>
                                            ))}
                                            <button onClick={addLocation} className="text-[10px] font-bold text-mint-600 uppercase flex items-center gap-1 hover:underline">
                                                <Plus size={12} /> Agregar Ubicación
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* IDENTIDAD BASIC */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Image size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Identidad & Contacto</span>
                        </div>
                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Nombre Tienda</label>
                                    <input
                                        data-onboarding-id="tienda-config-nombre"
                                        value={config.store_title || ""}
                                        onChange={(e) => update("store_title", e.target.value)}
                                        className="w-full border border-[color:var(--border)] rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Slug (URL)</label>
                                    <div className="flex items-center">
                                        <span className="bg-[var(--surface)] border border-r-0 border-[color:var(--border)] rounded-l-xl px-3 py-3 text-[var(--text-muted)] text-xs font-mono">/tienda/</span>
                                        <input
                                            value={config.slug || ""}
                                            onChange={(e) => update("slug", e.target.value)}
                                            className="w-full border border-[color:var(--border)] rounded-r-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none font-mono"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                                <div className="relative">
                                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                    <input
                                        data-onboarding-id="tienda-config-whatsapp"
                                        value={config.whatsapp || ""}
                                        onChange={(e) => update("whatsapp", e.target.value)}
                                        placeholder="WhatsApp (549...)"
                                        className="w-full border border-[color:var(--border)] rounded-xl pl-9 pr-4 py-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                                    />
                                </div>
                                <div className="relative">
                                    <Instagram size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                    <input
                                        value={config.instagram || ""}
                                        onChange={(e) => update("instagram", e.target.value)}
                                        placeholder="@usuario"
                                        className="w-full border border-[color:var(--border)] rounded-xl pl-9 pr-4 py-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                                    />
                                </div>
                                <div className="relative">
                                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                    <input
                                        value={config.notify_email || ""}
                                        onChange={(e) => update("notify_email", e.target.value)}
                                        placeholder="Email avisos"
                                        className="w-full border border-[color:var(--border)] rounded-xl pl-9 pr-4 py-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ESTILO VISUAL */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Palette size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Apariencia</span>
                        </div>
                        <div className="p-4 sm:p-5">
                            <div className="mb-4 border-b border-slate-100 pb-4">
                                <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] block mb-2">Diseño de Catálogo</label>
                                <div className="flex bg-[var(--surface-2)] p-1 rounded-xl">
                                    <button
                                        onClick={() => update('view_mode', 'grid')}
                                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${config.view_mode === 'grid' || !config.view_mode ? 'bg-[var(--surface)] shadow-sm text-[var(--text-default)]' : 'text-[var(--text-muted)]'}`}
                                    >
                                        <Grid size={14} /> Grilla
                                    </button>
                                    <button
                                        onClick={() => update('view_mode', 'list')}
                                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${config.view_mode === 'list' ? 'bg-[var(--surface)] shadow-sm text-[var(--text-default)]' : 'text-[var(--text-muted)]'}`}
                                    >
                                        <List size={14} /> Lista
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Color Principal</label>
                                    <input type="color" value={config.theme_config?.primaryColor || (isGastronomy ? '#f97316' : '#0f172a')} onChange={(e) => updateThemeField('primaryColor', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.primaryColor || (isGastronomy ? '#f97316' : '#0f172a')}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Fondo Tienda</label>
                                    <input type="color" value={config.theme_config?.storeBackgroundColor || '#f8fafc'} onChange={(e) => updateThemeField('storeBackgroundColor', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.storeBackgroundColor || '#f8fafc'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Tarjeta: Fondo</label>
                                    <input type="color" value={config.theme_config?.cardColors?.bg || '#ffffff'} onChange={(e) => updateCardTheme('bg', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.cardColors?.bg || '#ffffff'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Tarjeta: Bordes</label>
                                    <input type="color" value={config.theme_config?.cardColors?.border || '#e5e7eb'} onChange={(e) => updateCardTheme('border', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.cardColors?.border || '#e5e7eb'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Tarjeta: Texto</label>
                                    <input type="color" value={config.theme_config?.cardColors?.text || '#111827'} onChange={(e) => updateCardTheme('text', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.cardColors?.text || '#111827'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Modal: Fondo</label>
                                    <input type="color" value={config.theme_config?.modalColors?.bg || '#ffffff'} onChange={(e) => updateTheme('bg', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.modalColors?.bg || '#ffffff'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Modal: Bordes</label>
                                    <input type="color" value={config.theme_config?.modalColors?.border || '#e5e7eb'} onChange={(e) => updateTheme('border', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.modalColors?.border || '#e5e7eb'}</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Modal: Texto</label>
                                    <input type="color" value={config.theme_config?.modalColors?.text || '#111827'} onChange={(e) => updateTheme('text', e.target.value)} className="h-10 w-full rounded-lg cursor-pointer border border-[color:var(--border)] p-0.5 bg-[var(--surface)]" />
                                    <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">{config.theme_config?.modalColors?.text || '#111827'}</p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-md border border-[color:var(--border)] overflow-hidden shadow-sm">
                                <div className="px-4 py-3 bg-[var(--surface)] border-b border-slate-100 flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Preview en vivo</span>
                                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: config.theme_config?.primaryColor || '#0f172a' }}>Color principal</span>
                                </div>
                                <div className="p-4" style={{ backgroundColor: config.theme_config?.storeBackgroundColor || '#f8fafc' }}>
                                    <div className="rounded-md border p-4 shadow-sm" style={{ backgroundColor: config.theme_config?.cardColors?.bg || '#ffffff', borderColor: config.theme_config?.cardColors?.border || '#e5e7eb', color: config.theme_config?.cardColors?.text || '#111827' }}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-sm font-black">Tarjeta de producto</div>
                                                <div className="text-xs opacity-70">Vista previa del fondo y las tarjetas</div>
                                            </div>
                                            <button className="px-3 py-2 rounded-md text-xs font-black text-white shadow-md" style={{ backgroundColor: config.theme_config?.primaryColor || '#0f172a' }}>
                                                Comprar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2">Presets Rápidos:</p>
                                <div className="flex flex-wrap gap-2">
                                    {STORE_THEME_PRESETS.map((p, i) => (
                                        <button
                                            key={i}
                                            onClick={() => applyPreset(p)}
                                            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide border rounded-lg hover:scale-105 transition-transform shadow-sm flex items-center gap-2"
                                            style={{ backgroundColor: p.cardColors.bg, borderColor: p.cardColors.border, color: p.cardColors.text }}
                                        >
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* === COLUMNA DERECHA === */}
                <div className="lg:col-span-5 space-y-4">
                    {/* OPERATORIA */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Settings size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Reglas Operativas</span>
                        </div>
                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <span className="text-xs font-bold text-[var(--text-default)]">Mostrar stock al cliente</span>
                                <input
                                    type="checkbox"
                                    checked={config.show_stock}
                                    onChange={(e) => update("show_stock", e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Vender sin tener en cuenta stock</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Permite comprar aunque el stock actual sea cero o insuficiente</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.allow_checkout_without_stock || false}
                                    onChange={(e) => updateThemeField('allow_checkout_without_stock', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Avisar cuando queden pocas unidades</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Muestra un cartel en el carrito y antes de pagar para que el cliente confirme disponibilidad</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={lowStockAlert.enabled}
                                    onChange={(e) => updateThemeField('lowStockAlert', { ...lowStockAlert, enabled: e.target.checked })}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            {lowStockAlert.enabled && (
                                <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Avisar cuando el stock sea igual o menor a</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={lowStockAlert.threshold}
                                            onChange={(e) => updateThemeField('lowStockAlert', { ...lowStockAlert, threshold: e.target.value === '' ? '' : Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold bg-white focus:ring-2 focus:ring-slate-900 outline-none"
                                        />
                                        <span className="text-[10px] text-[var(--text-muted)] block mt-1">Con 1 avisa solo en la ultima unidad. Los productos sin stock informado nunca disparan el aviso.</span>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Texto del cartel</label>
                                        <textarea
                                            rows={3}
                                            value={lowStockAlert.message}
                                            onChange={(e) => updateThemeField('lowStockAlert', { ...lowStockAlert, message: e.target.value })}
                                            placeholder={DEFAULT_LOW_STOCK_ALERT.message}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-slate-900 outline-none resize-y"
                                        />
                                        <span className="text-[10px] text-[var(--text-muted)] block mt-1">
                                            {config.whatsapp
                                                ? 'Debajo del texto se muestra un boton de WhatsApp con tu numero.'
                                                : 'Carga tu WhatsApp en la pestana General para sumar un boton de contacto al cartel.'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Permitir que el cliente elija el día del pedido</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Muestra el calendario en el checkout y luego logística puede reprogramarlo</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.logistics?.request_date_enabled || false}
                                    onChange={(e) => updateSection('logistics', 'request_date_enabled', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Tienda siempre abierta</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Ignorar horarios y permitir compras 24/7</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.always_open}
                                    onChange={(e) => update("always_open", e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Mostrar precios en la tienda</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Si lo apagas, los clientes podran comprar pero no veran importes visibles.</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.showPrices !== false}
                                    onChange={(e) => updateThemeField('showPrices', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Pedir CUIT para ver precios</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Bloquea el catalogo hasta que el cliente ingrese un CUIT asociado y recien ahi habilita los importes.</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.requireCuitForPriceView || false}
                                    onChange={(e) => updateThemeField('requireCuitForPriceView', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Permitir filtrar por marca</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Muestra un filtro de marcas en la tienda para que el cliente acote el catalogo.</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.enable_brand_filter || false}
                                    onChange={(e) => updateThemeField('enable_brand_filter', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                                <div>
                                    <span className="text-xs font-bold text-[var(--text-default)] block">Permitir ordenar productos</span>
                                    <span className="text-[10px] text-[var(--text-muted)] block">Habilita ordenar el catalogo por nombre, precio o marca (de mayor a menor y viceversa).</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={config.theme_config?.enable_sorting || false}
                                    onChange={(e) => updateThemeField('enable_sorting', e.target.checked)}
                                    className="w-5 h-5 accent-slate-900 cursor-pointer"
                                />
                            </div>

                            <div className="p-3 border border-slate-100 rounded-xl bg-[var(--surface)]/70">
                                <label className="block text-xs font-bold text-[var(--text-default)] mb-1">
                                    Identificacion del cliente en checkout
                                </label>
                                <p className="text-[10px] text-[var(--text-muted)] mb-3">
                                    Elige si primero quieres pedir telefono o CUIT/DNI para reconocer la cuenta del cliente.
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => updateThemeField('checkoutIdentityMode', 'phone')}
                                        className={`px-3 py-2.5 rounded-md text-[10px] font-black uppercase tracking-widest border transition-all ${
                                            (config.theme_config?.checkoutIdentityMode || 'phone') === 'phone'
                                                ? 'bg-[var(--surface)] border border-[color:var(--border)] text-white border-[#18181B] shadow-sm'
                                                : 'bg-[var(--surface)] text-[var(--text-muted)] border-[color:var(--border)] hover:bg-[var(--surface-2)]'
                                        }`}
                                    >
                                        Telefono
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateThemeField('checkoutIdentityMode', 'document')}
                                        className={`px-3 py-2.5 rounded-md text-[10px] font-black uppercase tracking-widest border transition-all ${
                                            config.theme_config?.checkoutIdentityMode === 'document'
                                                ? 'bg-[var(--surface)] border border-[color:var(--border)] text-white border-[#18181B] shadow-sm'
                                                : 'bg-[var(--surface)] text-[var(--text-muted)] border-[color:var(--border)] hover:bg-[var(--surface-2)]'
                                        }`}
                                    >
                                        CUIT / DNI
                                    </button>
                                </div>
                            </div>

                            <div className="border border-slate-100 rounded-xl p-3 bg-amber-50/50">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <span className="text-xs font-bold text-[var(--text-default)] block">Popout de bienvenida</span>
                                        <span className="text-[10px] text-[var(--text-muted)] block">Se muestra al cliente al entrar a la tienda, una vez por ingreso.</span>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={config.theme_config?.welcomePopup?.enabled || false}
                                        onChange={(e) => updateSection('welcomePopup', 'enabled', e.target.checked)}
                                        className="w-5 h-5 accent-slate-900 cursor-pointer"
                                    />
                                </div>

                                {config.theme_config?.welcomePopup?.enabled && (
                                    <div className="mt-3 space-y-3 animate-in fade-in">
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Titulo</label>
                                            <input
                                                value={config.theme_config?.welcomePopup?.title || ''}
                                                onChange={(e) => updateSection('welcomePopup', 'title', e.target.value)}
                                                placeholder="Bienvenidos"
                                                className="w-full border border-[color:var(--border)] rounded-xl px-3 py-2 text-sm font-bold bg-[var(--surface)] focus:ring-2 focus:ring-slate-900 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Mensaje</label>
                                            <textarea
                                                rows="4"
                                                value={config.theme_config?.welcomePopup?.message || ''}
                                                onChange={(e) => updateSection('welcomePopup', 'message', e.target.value)}
                                                placeholder="Cuenta algo importante para tus clientes apenas ingresan."
                                                className="w-full border border-[color:var(--border)] rounded-xl px-3 py-2 text-sm bg-[var(--surface)] focus:ring-2 focus:ring-slate-900 outline-none resize-y"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Texto del boton</label>
                                            <input
                                                value={config.theme_config?.welcomePopup?.buttonText || ''}
                                                onChange={(e) => updateSection('welcomePopup', 'buttonText', e.target.value)}
                                                placeholder="Ver tienda"
                                                className="w-full border border-[color:var(--border)] rounded-xl px-3 py-2 text-sm font-bold bg-[var(--surface)] focus:ring-2 focus:ring-slate-900 outline-none"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-3 border border-slate-100 rounded-xl bg-[var(--surface-2)]/70">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-[var(--surface)] border border-[color:var(--border)] flex items-center justify-center shrink-0">
                                        <MapPin size={15} className="text-[var(--text-muted)]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <label className="block text-xs font-bold text-[var(--text-default)] mb-1">
                                            Sitio donde ingresan los pedidos web
                                        </label>
                                        <p className="text-[10px] text-[var(--text-muted)] mb-3">
                                            Este sitio se usa para la venta web, el stock, logística y el envío posterior a producción.
                                        </p>
                                        <select
                                            value={config.default_fulfillment_site_id || ""}
                                            onChange={(e) => update("default_fulfillment_site_id", e.target.value || null)}
                                            className="w-full border border-[color:var(--border)] rounded-xl px-3 py-3 text-sm font-bold bg-[var(--surface)] focus:ring-2 focus:ring-slate-900 outline-none"
                                        >
                                            <option value="">Seleccionar sitio</option>
                                            {sites.map((site) => (
                                                <option key={site.id} value={site.id}>
                                                    {site.nombre}
                                                </option>
                                            ))}
                                        </select>
                                        {sites.length === 0 ? (
                                            <p className="mt-2 text-[10px] text-amber-600 font-bold">
                                                No hay sitios activos para asignar. Primero creá una sucursal o sitio.
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* --- NUEVA SECCIÓN NOTIFICACIONES --- */}
                            <div className="border border-slate-100 rounded-xl p-3 bg-[var(--color-accent-soft)]/40">
                                <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] mb-3 flex items-center gap-1">
                                    <CreditCard size={12} /> Metodos de Pago Visibles
                                </label>
                                {paymentMethods.length === 0 ? (
                                    <p className="text-xs text-[var(--text-muted)]">No hay metodos de pago activos para esta organizacion.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {paymentMethods.map((pm) => {
                                            const enabled = (config.theme_config?.enabled_payment_methods || []).includes(pm.id);
                                            const clientLabel = config.theme_config?.payment_method_labels?.[pm.id] ?? pm.name;

                                            return (
                                                <div key={pm.id} className="rounded-xl border border-[color:var(--border)] bg-[var(--surface)] p-3">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <div className="text-xs font-bold text-[var(--text-default)]">{pm.name}</div>
                                                            <div className="text-[10px] text-[var(--text-muted)]">Nombre interno del metodo</div>
                                                        </div>
                                                        <input
                                                            type="checkbox"
                                                            checked={enabled}
                                                            onChange={() => togglePaymentMethod(pm.id)}
                                                            className="w-4 h-4 accent-slate-900 cursor-pointer"
                                                        />
                                                    </div>

                                                    {enabled && (
                                                        <div className="mt-3 animate-in fade-in">
                                                            <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Como lo ve el cliente</label>
                                                            <input
                                                                value={clientLabel}
                                                                onChange={(e) => updatePaymentMethodLabel(pm.id, e.target.value)}
                                                                className="w-full border border-[color:var(--border)] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                                                                placeholder={pm.name}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="border border-slate-100 rounded-xl p-3 bg-blue-50/50">
                                <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] mb-2 flex items-center gap-1">
                                    <Bell size={12} /> Notificaciones de Pedidos
                                </label>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Volume2 size={12} /> Sonido al entrar pedido</span>
                                        <input
                                            type="checkbox"
                                            checked={config.notification_config?.sound !== false}
                                            onChange={(e) => updateNotif("sound", e.target.checked)}
                                            className="w-4 h-4 accent-slate-900 cursor-pointer"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Monitor size={12} /> Aviso en Pantalla (Toast)</span>
                                        <input
                                            type="checkbox"
                                            checked={config.notification_config?.toast !== false}
                                            onChange={(e) => updateNotif("toast", e.target.checked)}
                                            className="w-4 h-4 accent-slate-900 cursor-pointer"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Bell size={12} /> Aviso con Gastify abierto</span>
                                        <input
                                            type="checkbox"
                                            checked={config.notification_config?.push === true}
                                            onChange={(e) => updateNotif("push", e.target.checked)}
                                            className="w-4 h-4 accent-slate-900 cursor-pointer"
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                    <PushDispositivosPanel />
                                </div>
                            </div>

                            {/* --- NUEVA SECCIÓN ESTIMACIÓN ENTREGA --- */}
                            <div className="border border-slate-100 rounded-xl p-3 bg-purple-50/50">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-[10px] font-bold uppercase text-[var(--text-muted)] flex items-center gap-1">
                                        <Timer size={12} /> Estimación de Entrega
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={config.delivery_estimation?.enabled || false}
                                        onChange={(e) => updateDeliveryEst("enabled", e.target.checked)}
                                        className="w-4 h-4 accent-slate-900 cursor-pointer"
                                    />
                                </div>

                                {config.delivery_estimation?.enabled && (
                                    <div className="flex gap-2 animate-in fade-in">
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                value={config.delivery_estimation?.time || ''}
                                                onChange={(e) => updateDeliveryEst("time", Number(e.target.value))}
                                                className="w-full bg-[var(--surface)] border border-[color:var(--border)] rounded-lg p-2 text-xs font-bold"
                                                placeholder="45"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <select
                                                value={config.delivery_estimation?.unit || 'minutos'}
                                                onChange={(e) => updateDeliveryEst("unit", e.target.value)}
                                                className="w-full bg-[var(--surface)] border border-[color:var(--border)] rounded-lg p-2 text-xs font-bold"
                                            >
                                                {isGastronomy ? (
                                                    <option value="minutos">Minutos</option>
                                                ) : (
                                                    <>
                                                        <option value="horas">Horas</option>
                                                        <option value="dias">Días</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                <p className="text-[9px] text-[var(--text-muted)] mt-1 italic">
                                    Se mostrará este tiempo estimado al cliente al finalizar la compra.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Costo Envío</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-xs font-bold">$</span>
                                        <input
                                            type="number"
                                            value={config.shipping_cost || 0}
                                            onChange={(e) => update("shipping_cost", Number(e.target.value))}
                                            className="w-full border border-[color:var(--border)] rounded-xl pl-8 pr-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Gratis desde</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-xs font-bold">$</span>
                                        <input
                                            type="number"
                                            value={config.free_shipping_threshold || ""}
                                            onChange={(e) => update("free_shipping_threshold", Number(e.target.value))}
                                            className="w-full border border-[color:var(--border)] rounded-xl pl-8 pr-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SELECTOR PROVEEDOR DE ENVIOS */}
                            <div>
                                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1 flex items-center gap-1">
                                    <Truck size={12} /> Proveedor de Envíos
                                </label>
                                <select
                                    className="w-full bg-[var(--surface)] border border-[color:var(--border)] p-2 rounded-md text-xs font-bold outline-none cursor-pointer focus:border-slate-400"
                                    value={config.shipping_provider_id || ""}
                                    onChange={(e) => update("shipping_provider_id", e.target.value)}
                                >
                                    <option value="">-- Ninguno / Logística Propia --</option>
                                    {providers.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.razon_social}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[9px] text-[var(--text-muted)] mt-1 italic">
                                    Los gastos de envío se asignarán a la Cta. Cte. de este proveedor.
                                </p>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">Texto Promocional</label>
                                <textarea
                                    value={config.financing_text || ""}
                                    onChange={(e) => update("financing_text", e.target.value)}
                                    placeholder="Ej: 3 Cuotas sin interés con Visa"
                                    className="w-full border border-[color:var(--border)] rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none resize-none h-20"
                                />
                            </div>

                            {/* --- DATOS BANCARIOS --- */}
                            <div className="mt-4 border-t border-slate-100 pt-4">
                                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1 flex items-center gap-1">
                                    <Landmark size={12} /> Cuenta Bancaria para Transferencias
                                </label>

                                <div className="mb-2">
                                    <select
                                        className="w-full bg-[var(--surface)] border border-[color:var(--border)] p-2 rounded-md text-xs font-bold outline-none cursor-pointer focus:border-slate-400"
                                        value={config.default_bank_id || ""}
                                        onChange={(e) => handleBankSelection(e.target.value)}
                                    >
                                        <option value="">-- Sin cuenta asociada --</option>
                                        {banks.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {b.name} ({b.currency})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <textarea
                                    value={config.bank_details || ""}
                                    onChange={(e) => update("bank_details", e.target.value)}
                                    placeholder="CBU, Alias, Banco... (Este texto se mostrará al cliente)"
                                    className="w-full border border-[color:var(--border)] rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-slate-900 outline-none resize-none h-24"
                                />
                            </div>
                        </div>
                    </div>

                    {/* HORARIOS */}
                    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
                        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
                            <Clock size={16} className="text-[var(--text-muted)]" />
                            <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Horarios de Atención</span>
                        </div>

                        <div className="p-4 border-b border-slate-50 flex flex-wrap gap-2">
                            {DAYS_MAP.map((d) => (
                                <button
                                    key={d.key}
                                    onClick={() => setActiveDay(d.key)}
                                    className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeDay === d.key
                                            ? 'bg-slate-900 text-white shadow-md'
                                            : 'bg-[var(--surface)] border border-[color:var(--border)] text-[var(--text-muted)] hover:border-[color:var(--border)]'
                                        }`}
                                >
                                    {d.label}
                                </button>
                            ))}
                        </div>

                        <div className="p-4 sm:p-5 bg-[var(--surface-2)] min-h-[180px]">
                            <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                                <span className="text-[10px] font-black uppercase text-[var(--text-muted)] tracking-wider">
                                    Turnos para {DAYS_MAP.find(d => d.key === activeDay)?.label}
                                </span>
                                <button
                                    onClick={addSlot}
                                    className="px-3 py-1.5 bg-[var(--surface)] border border-[color:var(--border)] rounded-lg text-[10px] font-bold uppercase tracking-wide text-mint-600 hover:bg-blue-50 flex items-center gap-1 shadow-sm"
                                >
                                    <Plus size={14} /> Agregar
                                </button>
                            </div>

                            <div className="space-y-3">
                                {currentSlots.length === 0 && (
                                    <div className="text-center py-8 flex flex-col items-center justify-center text-xs text-[var(--text-muted)] italic border border-dashed border-[color:var(--border)] rounded-xl bg-[var(--surface)]">
                                        <Clock size={24} className="mb-2 opacity-20" />
                                        Cerrado este día
                                    </div>
                                )}

                                {currentSlots.map((slot, idx) => (
                                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-[var(--surface)] p-3 rounded-xl border border-[color:var(--border)] shadow-sm animate-in fade-in slide-in-from-top-2">
                                        <div className="flex-1">
                                            <label className="text-[9px] font-bold text-[var(--text-muted)] uppercase block mb-1">Abre</label>
                                            <input
                                                type="time"
                                                value={slot.from}
                                                onChange={(e) => changeSlotValue(idx, 'from', e.target.value)}
                                                className="w-full border border-[color:var(--border)] rounded-lg px-2 py-1.5 text-xs font-bold text-[var(--text-default)] outline-none focus:border-slate-400"
                                            />
                                        </div>
                                        <span className="text-slate-300 text-xs mt-4">➜</span>
                                        <div className="flex-1">
                                            <label className="text-[9px] font-bold text-[var(--text-muted)] uppercase block mb-1">Cierra</label>
                                            <input
                                                type="time"
                                                value={slot.to}
                                                onChange={(e) => changeSlotValue(idx, 'to', e.target.value)}
                                                className="w-full border border-[color:var(--border)] rounded-lg px-2 py-1.5 text-xs font-bold text-[var(--text-default)] outline-none focus:border-slate-400"
                                            />
                                        </div>
                                        <button
                                            onClick={() => removeSlot(idx)}
                                            className="p-2 text-rose-400 hover:bg-rose-50 rounded-lg transition-colors mt-0 sm:mt-4 self-end sm:self-auto"
                                            title="Eliminar turno"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
        </div>
    );
}
